import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react';
import { User } from '../types';
import { api } from '../services/api';
import {
    handleUserLogout,
    handleUserLogin,
    updateUserProperties,
} from '../utils/analytics';
import logger, { enableDebugModeForTestAccount } from '../utils/logger';
interface UserContextType {
    user: User | null;
    setUser: (user: User | null) => void;
    logout: () => void;
    isLoading: boolean;
    restoreSession: () => Promise<User | null>;
    getProfileImageUrl: () => string;
    /** Check if user has a paid plan (not Free) - use for showing upgrade options */
    isPaidPlan: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Default profile pictures (now stored locally in pair app)
const NUM_OF_DEFAULT_PROFILES = 8;
const DEFAULT_PROFILE_PATH = '/images/default-profiles/profile';

export function UserProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Restore session on mount
    useEffect(() => {
        restoreSession();
    }, []);

    const restoreSession = async (): Promise<User | null> => {
        try {
            if (typeof window === 'undefined') {
                setIsLoading(false);
                return null;
            }

            // Check for stored JWT token
            const storedToken = localStorage.getItem('jwtToken');
            const storedUser = localStorage.getItem('pairUser');

            if (!storedToken || !storedUser) {
                setIsLoading(false);
                return null;
            }

            // Parse stored user data
            const userData = JSON.parse(storedUser);

            // IMPORTANT: Set the JWT token in the API service for all subsequent API calls
            api.setJwtToken(storedToken);
            logger.debug(
                '[UserContext] JWT token restored and set in API service'
            );

            // Validate the token is still valid by making a test request
            try {
                // You might want to add a /users/me endpoint for this
                // For now, we'll trust the stored data if it exists
                setUser(userData);
                setIsLoading(false);

                // Auto-enable debug mode for test accounts on session restore
                enableDebugModeForTestAccount(userData.test_account === true);

                // Update PostHog user properties on session restore
                // This ensures properties like test_account are always set
                updateUserProperties(userData.id, userData.email, {
                    name: userData.name,
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    profile_path: userData.profile_path,
                    test_account: userData.test_account,
                    plan_display_name: userData.plan_display_name,
                    plan_type: userData.plan_type,
                    account_type: userData.account_type,
                    plan_upgradable: userData.plan_upgradable,
                });

                return userData;
            } catch (error) {
                console.error('[UserContext] Token validation failed:', error);
                // Clear invalid session
                localStorage.removeItem('jwtToken');
                localStorage.removeItem('pairUser');
                setUser(null);
                setIsLoading(false);
                return null;
            }
        } catch (error) {
            console.error('[UserContext] Error restoring session:', error);
            setIsLoading(false);
            return null;
        }
    };

    const updateUser = (newUser: User | null) => {
        const previousUser = user;
        setUser(newUser);

        if (newUser) {
            logger.debug('[UserContext] Updating user:', {
                userId: newUser.id,
                userEmail: newUser.email,
                hasToken: !!newUser.token,
                tokenLength: newUser.token ? newUser.token.length : 0,
            });

            // Store user data and token
            if (typeof window !== 'undefined') {
                localStorage.setItem('pairUser', JSON.stringify(newUser));
                if (newUser.token) {
                    logger.debug(
                        '[UserContext] Storing JWT token in localStorage and API service'
                    );
                    localStorage.setItem('jwtToken', newUser.token);
                    api.setJwtToken(newUser.token);
                } else {
                    console.warn('[UserContext] ⚠️ User object has no token!');
                }
                // Store user ID and email for analytics
                localStorage.setItem('pair_user_id', newUser.id);
                localStorage.setItem('pair_user_email', newUser.email);

                // If this is a new login (not just updating existing user)
                if (!previousUser || previousUser.id !== newUser.id) {
                    // Handle analytics login to start new session with full properties
                    handleUserLogin(newUser.id, newUser.email, {
                        name: newUser.name,
                        first_name: newUser.first_name,
                        last_name: newUser.last_name,
                        profile_path: newUser.profile_path,
                        test_account: newUser.test_account,
                        plan_display_name: newUser.plan_display_name,
                        plan_type: newUser.plan_type,
                        account_type: newUser.account_type,
                        plan_upgradable: newUser.plan_upgradable,
                    });

                    // Auto-enable debug mode for test accounts in production
                    enableDebugModeForTestAccount(
                        newUser.test_account === true
                    );
                }
            }
        } else {
            // Clear all stored data on logout
            if (typeof window !== 'undefined') {
                // Clear ALL localStorage except known third-party keys
                // PostHog handles its own reset via posthog.reset() in handleUserLogout
                const thirdPartyPrefixes = ['ph_', 'debug', 'honey'];
                const keysToRemove: string[] = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key) {
                        const isThirdParty = thirdPartyPrefixes.some((prefix) =>
                            key.startsWith(prefix)
                        );
                        if (!isThirdParty) {
                            keysToRemove.push(key);
                        }
                    }
                }
                keysToRemove.forEach((key) => localStorage.removeItem(key));

                // Clear all sessionStorage
                sessionStorage.clear();

                // Clear JWT token in API service
                api.setJwtToken('');

                logger.debug(
                    `[UserContext] Cleared ${keysToRemove.length} localStorage items and all sessionStorage`
                );
            }
        }
    };

    const logout = async () => {
        // First handle analytics session termination
        handleUserLogout();

        // Call server signOut endpoint to end server session
        try {
            await api.signOut();
            logger.debug('[UserContext] Server session ended successfully');
        } catch (error) {
            console.error('[UserContext] Failed to end server session:', error);
            // Continue with local cleanup even if server signout fails
        }

        // Clear user data and all storage
        updateUser(null);
    };

    const getProfileImageUrl = (): string => {
        if (!user) {
            return `${DEFAULT_PROFILE_PATH}0.png`; // Default image
        }

        // Check if user has a custom profile image (full URL)
        if (user.profile_path) {
            // If it's already a full URL, return as is
            if (user.profile_path.startsWith('http')) {
                return user.profile_path;
            }
            // Otherwise it's a relative path from the API
            return user.profile_path;
        }

        // Use default profile based on user ID (matching web app logic)
        const profileIndex = parseInt(user.id) % NUM_OF_DEFAULT_PROFILES;
        return `${DEFAULT_PROFILE_PATH}${profileIndex}.png`;
    };

    /**
     * Check if user has a paid plan (not Free)
     * Used to determine if upgrade options should be hidden
     * Returns true if user has a paid subscription (Pro, Plus, etc.)
     */
    const isPaidPlan = (): boolean => {
        return !!(
            user?.plan_display_name &&
            user.plan_display_name.toLowerCase() !== 'free'
        );
    };

    return (
        <UserContext.Provider
            value={{
                user,
                setUser: updateUser,
                logout,
                isLoading,
                restoreSession,
                getProfileImageUrl,
                isPaidPlan,
            }}
        >
            {children}
        </UserContext.Provider>
    );
}

export function useUser() {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}
